import { useRef, useState } from "react";
import Button from "../common/Button";
import Input from "../common/Input";
import CustomSelect from "../common/CustomSelect";
import FileUpload from "../common/FileUpload";
import { createNews } from "../../../services/newsServices";
import { toast } from "react-hot-toast";
import RichTextEditor from "../common/RichTextEditor";
import { useNavigate } from "react-router";

const publicOptions = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const galleryNameOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const NewsForm = () => {
  const navigate = useNavigate();
  const imageRef = useRef();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [galleryName, setGalleryName] = useState(galleryNameOptions[0]);
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState(null);
  const [image, setImage] = useState([]);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    title: "",
    date: "",
    galleryName: "",
    description: "",
    visibility: "",
    image: "",
  });

  // Validate all fields
  const validate = () => {
    const newErrors = {};
    if (!title) newErrors.title = "Title is required";
    if (!date) newErrors.date = "Date is required";
    if (!galleryName) newErrors.galleryName = "Please choose Yes or No";
    if (!description) newErrors.description = "Description is required";
    if (!visibility) newErrors.visibility = "Please select Public or Private";
    if (!image || image.length === 0)
      newErrors.image = "Please upload at least one image";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      title,
      description,
      date,
      showOnHomepage: galleryName.value === "yes",
      imageUrl: image,
      visibility: visibility.value,
      status: "approved",
    };

    try {
      setLoading(true);
      await createNews(payload);
      toast.success("News created successfully!");

      // Reset form
      setTitle("");
      setDate("");
      setGalleryName(galleryNameOptions[0]);
      setDescription("");
      setVisibility(null);
      setImage([]);
      setErrors({});
      window.location.href = "/admin/news/list";
    } catch (error) {
      toast.error(error.message || "Failed to create news");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F5F0FF] p-6 rounded-lg font-sans space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Title"
          value={title}
          onChange={(val) => {
            setTitle(val);
            if (errors.title) setErrors({ ...errors, title: "" });
          }}
          required
          error={errors.title}
        />
        <Input
          label="Date"
          type="date"
          value={date}
          onChange={(val) => {
            setDate(val);
            if (errors.date) setErrors({ ...errors, date: "" });
          }}
          required
          error={errors.date}
        />
        <CustomSelect
          label="Show on Home page ?"
          value={galleryName}
          onChange={(val) => {
            setGalleryName(val);
            if (errors.galleryName) setErrors({ ...errors, galleryName: "" });
          }}
          options={galleryNameOptions}
          placeholder="Yes / No"
          error={errors.galleryName}
        />
      </div>

      <RichTextEditor
        description={description}
        setDescription={(val) => {
          setDescription(val);
          if (errors.description) setErrors({ ...errors, description: "" });
        }}
      />
      {errors.description && (
        <p className="text-red font-semibold text-sm mt-1">
          {errors.description}
        </p>
      )}

      <div className="grid grid-cols-1 w-1/2 md:grid-cols-2 gap-4">
        <CustomSelect
          label="Public / Private"
          value={visibility}
          onChange={(val) => {
            setVisibility(val);
            if (errors.visibility) setErrors({ ...errors, visibility: "" });
          }}
          options={publicOptions}
          placeholder="Select"
          error={errors.visibility}
        />
      </div>

      <FileUpload
        ref={imageRef}
        mode="image"
        defaultLabel="Choose Images"
        onUpload={() => {}}
        setImage={(img) => {
          setImage(img);
          if (errors.image) setErrors({ ...errors, image: "" });
        }}
        image={image}
      />
      {errors.image && (
        <p className="text-red font-semibold text-sm mt-1">{errors.image}</p>
      )}

      <div className="pt-4 space-y-2">
        <Button className="w-full" onClick={handleSubmit} disabled={loading}>
          {loading ? "Submitting..." : "Confirm & Add News"}
        </Button>
        <Button
          onClick={() => navigate("/admin/news/list")}
          className="w-full"
          variant="text"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default NewsForm;
